import React from "react"
import { type ApolloError } from "@apollo/client"
import { type Transaction } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgBuilding02 } from "@digits-shared/components/SVGIcons/line/Building02.svg"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import { SvgLayersTwo01 } from "@digits-shared/components/SVGIcons/line/LayersTwo01.svg"
import { IconSize } from "@digits-shared/components/UI/Icons/Icon"
import { AlertModal } from "@digits-shared/DesignSystem/Modals/AlertModal"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import numberHelper from "@digits-shared/helpers/numberHelper"
import { useOnEnter } from "@digits-shared/hooks/useOnEnter"
import colors from "@digits-shared/themes/colors"
import { BodyText, DetailText, LabelText } from "@digits-shared/themes/typography"
import dayjs from "dayjs"
import styled from "styled-components"
import { PartyInfo } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/WorkItemTransaction/WorkItemPartyDetails"
import { useMatchTransactions } from "src/frontend/components/OS/Applications/Shared/Accounting/Matching/hooks/useMatchTransactions"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import { PartyIcon } from "src/shared/components/PartyHover/PartyIcon"

/*
  STYLES
*/

const TransactionsDisplayContainer = styled.div`
  margin-top: 23px;

  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
`

const TransactionDisplayRow = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid ${colors.secondary20};

  &:hover {
    background-color: ${colors.secondary10};
    cursor: pointer;
  }
`

const SideContainer = styled.div<{ gap: number }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ gap }) => gap}px;
`

const Date = styled(DetailText)`
  color: ${colors.secondary};
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PartyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 11px;
`

const MissingPartyIconWrapper = styled.div`
  border-radius: 4px;
  border: 1px dashed ${colors.secondary50};
  width: 24px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const MissingPartyIcon = styled(SvgBuilding02)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.secondary50)};
`

const Chevron = styled(SvgChevronRight)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.secondary, 2)};
`

/*
  INTERFACES
*/

interface Props {
  transactions: Transaction[]
  onCancel: () => void
  onMatch?: (transactions: Transaction[]) => void
  onError?: (error: ApolloError) => void
}

/*
  COMPONENTS
*/

export const MatchModal: React.FC<Props> = ({ transactions, onMatch, onCancel, onError }) => {
  const match = useMatchTransactions({ transactions, onMatch, onError })
  const daysApart = React.useMemo(() => {
    if (transactions.length < 2 || !transactions[0] || !transactions[1]) {
      return 0
    }

    const date1 = dayjs.unix(transactions[0].timestamp).utc()
    const date2 = dayjs.unix(transactions[1].timestamp).utc()

    return Math.abs(date1.diff(date2, "day"))
  }, [transactions])

  return (
    <AlertModal
      boldHeader
      headerLabel="Match Transactions"
      HeaderIcon={SvgLayersTwo01}
      primaryButtonLabel="Match"
      primaryButtonClick={match}
      secondaryButtonLabel="Cancel"
      secondaryButtonClick={onCancel}
    >
      <BodyText>
        {daysApart >= 10 ? (
          <>
            These transactions are dated <strong>{daysApart} days apart</strong> from one another.
            Are you sure you want to match them?
          </>
        ) : (
          <>
            By matching transactions, you will combine them into one transaction and remove
            duplicates. Are you sure you want to proceed?
          </>
        )}
      </BodyText>
      <TransactionsDisplay transactions={transactions} />
    </AlertModal>
  )
}

const TransactionsDisplay: React.FC<{
  transactions: Transaction[]
}> = ({ transactions }) => (
  <TransactionsDisplayContainer>
    {transactions.map((transaction) => (
      <TransactionDisplay key={transaction.factId} transaction={transaction} />
    ))}
  </TransactionsDisplayContainer>
)

const TransactionDisplay: React.FC<{
  transaction: Transaction
}> = ({ transaction }) => {
  const formattedDate = React.useMemo(
    () => dayjs.unix(transaction.timestamp).utc().format("MMM D"),
    [transaction.timestamp]
  )

  const generatePath = useFrontendPathGenerator()

  const onClick = React.useCallback(() => {
    window.open(
      generatePath(routes.accountingDetailEdit, {
        transactionId: transaction.factId,
      }),
      "_blank"
    )
  }, [generatePath, transaction.factId])

  const onKeydown = useOnEnter(onClick)

  return (
    <TransactionDisplayRow onClick={onClick} onKeyDown={onKeydown} role="button" tabIndex={0}>
      <SideContainer gap={24}>
        <Date weight="heavy">{formattedDate}</Date>
        <PartyRow>
          {transaction.party ? (
            <PartyIcon party={transaction.party} size={IconSize.SmallMedium} />
          ) : (
            <MissingPartyIconWrapper>
              <MissingPartyIcon />
            </MissingPartyIconWrapper>
          )}
          <PartyInfo>
            <BodyText weight="medium">{transaction.party?.name || "No Party"}</BodyText>
            <LabelText color={colors.secondary60}>{transaction.description}</LabelText>
          </PartyInfo>
        </PartyRow>
      </SideContainer>
      <SideContainer gap={8}>
        <div>
          {numberHelper.currency(monetaryValueHelper.absoluteAmount(transaction.moneyFlow.value))}
        </div>
        <Chevron />
      </SideContainer>
    </TransactionDisplayRow>
  )
}
